import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Home, MessageSquare, Settings, Users, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FloorVideoPlayer } from '@/components/FloorVideoPlayer';
import { FloorChangeModal } from '@/components/FloorChangeModal';
import { OutageApologyModal } from '@/components/OutageApologyModal';
import { MaintenanceCompensationModal } from '@/components/MaintenanceCompensationModal';
import { getTheme, FloorTheme } from '@/lib/themes';
import api, { getMaintenanceCompensation, MaintenanceCompensation } from '@/lib/api';
import { ensurePushSubscription } from '@/lib/push';
import { resolveStartPath } from '@/lib/startPage';
import { HeaderWithMailbox } from './HeaderWithMailbox';
import { useI18n } from '@/lib/i18n';
import { getMutedTextColor, getPanelStyle, getSoftSurface, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

export function Layout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [maintenanceCompensation, setMaintenanceCompensation] = useState<MaintenanceCompensation | null>(null);
    const [theme, setTheme] = useState<FloorTheme>(getTheme('User', 3)); // Default
    const [showMovie, setShowMovie] = useState(false);
    const [floorChange, setFloorChange] = useState<{ from: number; to: number } | null>(null);
    const [showFloorChangeModal, setShowFloorChangeModal] = useState(false);
    const [floorChangeMode, setFloorChangeMode] = useState<'promotion' | 'demotion' | 'welcome' | null>(null);
    const startPageRedirectAppliedRef = useRef(false);
    const videoPlaybackKeyRef = useRef<string | null>(null);
    const { lang, setLang, t } = useI18n();

    const isAuthPage = ['/login', '/register', '/verify-email'].includes(location.pathname);

    // Fetch latest user data on mount to ensure permissions/status are up to date
    useEffect(() => {
        if (isAuthPage) return;
        if (!localStorage.getItem('token')) {
            localStorage.removeItem('user');
            setUser(null);
            navigate('/login', { replace: true });
            return;
        }
        checkUserStatus();
    }, [location.pathname]);

    const syncVideoState = (u: any) => {
        if (!u || isAuthPage) return;
        const today = new Date().toISOString().split('T')[0];
        const storageKeyDate = `maison_last_date_${u.user_id}`;
        const storageKeyFloor = `maison_last_floor_${u.user_id}`;

        const lastDate = localStorage.getItem(storageKeyDate);
        const lastFloor = localStorage.getItem(storageKeyFloor);
        const isStaff = ['Admin', 'Maestro', 'Manager'].includes(u.role_type);
        const hasPrevFloor = lastFloor !== null && !Number.isNaN(Number(lastFloor));
        const isFloorChanged = hasPrevFloor && lastFloor !== String(u.floor_level);

        const shouldPlay = lastDate !== today || lastFloor !== String(u.floor_level);
        const playbackKey = `${u.user_id}:${today}:${u.floor_level}`;
        // User state can arrive from multiple legitimate sources close together
        // (initial status, route refresh, and user-updated events). Reserve the
        // playback cycle before rendering so a second status response cannot
        // re-open the same video during the completion delay.
        if (shouldPlay && videoPlaybackKeyRef.current !== playbackKey) {
            videoPlaybackKeyRef.current = playbackKey;
            setShowMovie(true);
            if (!isStaff && isFloorChanged) {
                setFloorChange({ from: Number(lastFloor), to: u.floor_level });
            } else {
                setFloorChange(null);
            }
        }
    };

    const applyUserState = (u: any) => {
        if (!u) return;
        setUser(u);
        localStorage.setItem('user', JSON.stringify(u));
        setTheme(getTheme(u.role_type, u.floor_level));
        syncVideoState(u);
    };

    const checkMaintenanceCompensation = async () => {
        try {
            const response = await getMaintenanceCompensation();
            setMaintenanceCompensation(response.data?.pending ? response.data : null);
        } catch (error) {
            // Keep any already-loaded pending notice visible on a transient
            // failure.  The server remains authoritative after a refresh.
            console.error('Maintenance compensation status check failed', error);
        }
    };

    const checkUserStatus = async () => {
        // Layout is mounted before effects run.  Refuse the authenticated
        // status request synchronously when no bearer token exists so a clean
        // visit never emits an expected-but-noisy 401 or races the login form.
        if (!localStorage.getItem('token')) {
            handleLogout();
            return;
        }
        try {
            const res = await api.get('/users/me');
            const u = res.data;
            applyUserState(u);
            await syncTimezone(u);
            await checkMaintenanceCompensation();
            ensurePushSubscription();
        } catch (e: any) {
            // If the user is suspended (400) or unauthorized (401/403), force logout
            if (e.response && (e.response.status === 400 || e.response.status === 401 || e.response.status === 403)) {
                handleLogout(); // Force logout
                return;
            }
            console.error("Status check failed", e);
        }
    };

    // Listen for 403 Suspended event (custom event or api interceptor)
    // For now, let's just implement the tab logic based on the user object.

    useEffect(() => {
        const handleUserUpdated = (event: Event) => {
            if (!isAuthPage) {
                const updatedUser = (event as CustomEvent).detail;
                if (updatedUser) {
                    applyUserState(updatedUser);
                } else {
                    checkUserStatus();
                }
            }
        };
        window.addEventListener('maison:user-updated', handleUserUpdated as EventListener);
        return () => {
            window.removeEventListener('maison:user-updated', handleUserUpdated as EventListener);
        };
    }, [isAuthPage]);

    useEffect(() => {
        if (isAuthPage || !user) return;
        if (user.must_change_credentials && location.pathname !== '/') {
            navigate('/', { replace: true });
            return;
        }
        if (location.pathname !== '/') return;
        if (startPageRedirectAppliedRef.current) return;
        startPageRedirectAppliedRef.current = true;
        const targetPath = resolveStartPath(user);
        if (targetPath !== '/' && targetPath !== location.pathname) {
            navigate(targetPath, { replace: true });
        }
    }, [isAuthPage, user, location.pathname, navigate]);

    useEffect(() => {
        // Load user from storage initially
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const u = JSON.parse(storedUser);
                setUser(u);

                // Determine theme
                const newTheme = getTheme(u.role_type, u.floor_level);
                setTheme(newTheme);
                // Cached state is only for immediate layout continuity. The
                // authoritative /users/me response owns video scheduling.
            } catch (e) {
                // Invalid user data
            }
        }
    }, [location.pathname]);

    const syncTimezone = async (u: any) => {
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            if (tz && u && u.timezone !== tz) {
                await api.patch('/users/me', { timezone: tz });
                const nextUser = { ...u, timezone: tz };
                setUser(nextUser);
                localStorage.setItem('user', JSON.stringify(nextUser));
            }
        } catch (e) {
            // Ignore timezone sync failures
        }
    };

    const handleVideoComplete = () => {
        setShowMovie(false);
        if (user) {
            const today = new Date().toISOString().split('T')[0];
            const lastFloor = localStorage.getItem(`maison_last_floor_${user.user_id}`);
            const isStaff = ['Admin', 'Maestro', 'Manager'].includes(user.role_type);
            const welcomeKey = `maison_welcome_shown_${user.user_id}`;
            const hasWelcome = localStorage.getItem(welcomeKey);
            if (!isStaff && !hasWelcome) {
                setFloorChange({ from: user.floor_level, to: user.floor_level });
                setFloorChangeMode('welcome');
                setShowFloorChangeModal(true);
                localStorage.setItem(welcomeKey, '1');
            } else if (!isStaff && lastFloor && lastFloor !== String(user.floor_level)) {
                const fromValue = Number(lastFloor);
                setFloorChange({ from: fromValue, to: user.floor_level });
                setFloorChangeMode(user.floor_level > fromValue ? 'promotion' : 'demotion');
                setShowFloorChangeModal(true);
            }
            localStorage.setItem(`maison_last_date_${user.user_id}`, today);
            localStorage.setItem(`maison_last_floor_${user.user_id}`, String(user.floor_level));
        }
    };

    const handleFloorChangeClose = () => {
        setShowFloorChangeModal(false);
        setFloorChange(null);
        setFloorChangeMode(null);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setMaintenanceCompensation(null);
        videoPlaybackKeyRef.current = null;
        navigate('/login', { replace: true });
    };

    // Do not render the protected outlet even for one frame without a token.
    // This closes the remaining pre-effect race shared by every protected page.
    if (!isAuthPage && !localStorage.getItem('token')) {
        return <Navigate to="/login" replace />;
    }

    if (isAuthPage) {
        return <Outlet />;
    }

    const navItems = [
        { path: '/', label: t('ホーム'), icon: <Home size={20} /> },
        { path: '/neighbors', label: t('隣人'), icon: <Users size={20} /> },
        { path: '/salon', label: t('サロン'), icon: <MessageSquare size={20} />, requiresSalon: true },
        { path: '/toku', label: 'Toku', icon: <Trophy size={20} /> },
        { path: '/u-store', label: 'U-Store', icon: <Trophy size={20} />, requiresUnito: true },
        { path: '/settings', label: t('制御室'), icon: <Settings size={20} /> },
        // Theater removed as per request
        // { path: '/theater', label: 'Theater', icon: <Video size={20} /> },
        { path: '/admin', label: t('管理'), icon: <Settings size={20} />, role: ['Admin'] },
        { path: '/ai-admin', label: t('AI管理'), icon: <Settings size={20} />, role: ['Admin', 'Manager'] },
        { path: '/contact', label: t('管理人へ連絡'), icon: <MessageSquare size={20} /> },
    ];

    const canSee = (item: any) => {
        if (!user) return false;
        if (item.requiresChat && !user.can_access_chat) return false;
        if (item.requiresSalon && !user.salon_enabled) return false;
        if (item.requiresUnito && !(user.unito_enabled || user.ustore_enabled)) return false;
        if (item.role && !item.role.includes(user.role_type)) return false;
        return true;
    };

    const darkTheme = isDarkTheme(theme);
    const mobileMenuStyle = getPanelStyle(theme, 0.98, 0.94);
    const mobileMenuMutedText = getMutedTextColor(theme);
    const mobileMenuPrimaryText = pickTextColor(theme.colors.primary);

    return (
        <div
            className={`h-[100dvh] overflow-hidden flex flex-col text-slate-800 transition-colors duration-500 relative ${theme.font}`}
            style={{
                color: theme.colors.text
            }}
        >
            {/* Background Image Layer */}
            <div
                className="fixed inset-0 z-[-2] bg-cover bg-center transition-all duration-1000 ease-in-out transform scale-105"
                style={{
                    backgroundImage: `url(${theme.bgImage})`,
                }}
            />
            {/* Overlay Layer for readability & Patterns */}
            <div
                className={`fixed inset-0 z-[-1] pointer-events-none transition-all duration-1000 ${theme.containerClass}`}
                style={{
                    backgroundColor: theme.colors.background,
                    opacity: 0.85 // High opacity (low transparency) to ensure readability, but allow image to bleed through
                }}
            ></div>

            {/* Feature Video Overlay */}
            {showMovie && (
                <FloorVideoPlayer
                    hlsSrc={theme.movieHlsPath}
                    fallbackSrc={theme.moviePath}
                    onComplete={handleVideoComplete}
                />
            )}

            {floorChange && (
                <FloorChangeModal
                    isOpen={showFloorChangeModal}
                    onClose={handleFloorChangeClose}
                    theme={theme}
                    fromFloor={floorChange.from}
                    toFloor={floorChange.to}
                    mode={floorChangeMode || undefined}
                />
            )}

            <MaintenanceCompensationModal
                compensation={maintenanceCompensation}
                onAcknowledged={(result) => {
                    setMaintenanceCompensation(null);
                    if (user) {
                        applyUserState({
                            ...user,
                            smile_score: result.current_smile_score,
                            floor_level: result.current_floor_level,
                        });
                    }
                }}
            />

            {!maintenanceCompensation && (
                <OutageApologyModal
                    user={user}
                    onAcknowledged={(updated) => applyUserState(updated)}
                />
            )}

            {/* Header */}
            <HeaderWithMailbox user={user} theme={theme} isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} handleLogout={handleLogout} />


            {/* Mobile Nav */}
            {isMobileMenuOpen && (
                <div
                    className="md:hidden fixed inset-x-4 bottom-4 top-[88px] z-40 overflow-y-auto rounded-3xl border p-4 shadow-2xl"
                    style={mobileMenuStyle}
                >
                    <nav className="flex flex-col gap-3">
                        {navItems.filter(canSee).map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-medium transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{
                                    color: location.pathname === item.path ? theme.colors.primary : theme.colors.text,
                                    borderColor: location.pathname === item.path ? theme.colors.primary : withAlpha(theme.colors.border, 0.7),
                                    backgroundColor: location.pathname === item.path ? withAlpha(theme.colors.primary, darkTheme ? 0.18 : 0.12) : 'transparent',
                                }}
                            >
                                {item.icon}
                                {item.label}
                            </Link>
                        ))}
                        <div
                            className="mt-1 rounded-2xl border px-4 py-3"
                            style={{
                                borderColor: withAlpha(theme.colors.border, 0.7),
                                backgroundColor: getSoftSurface(theme, 0.84, 0.48),
                            }}
                        >
                            <div className="mb-2 text-[11px] uppercase tracking-[0.24em]" style={{ color: mobileMenuMutedText }}>
                                Language
                            </div>
                            <div className="flex items-center gap-1 rounded-full border px-2 py-1 text-[10px]" style={{ borderColor: theme.colors.border }}>
                                <button
                                    className="rounded-full px-2 py-0.5"
                                    onClick={() => setLang('en')}
                                    type="button"
                                    style={{
                                        backgroundColor: lang === 'en' ? theme.colors.primary : 'transparent',
                                        color: lang === 'en' ? mobileMenuPrimaryText : mobileMenuMutedText,
                                    }}
                                >
                                    E
                                </button>
                                <button
                                    className="rounded-full px-2 py-0.5"
                                    onClick={() => setLang('ja')}
                                    type="button"
                                    style={{
                                        backgroundColor: lang === 'ja' ? theme.colors.primary : 'transparent',
                                        color: lang === 'ja' ? mobileMenuPrimaryText : mobileMenuMutedText,
                                    }}
                                >
                                    J
                                </button>
                            </div>
                        </div>
                        <Button
                            variant="destructive"
                            className="mt-2 w-full"
                            onClick={handleLogout}
                            style={{
                                backgroundColor: withAlpha('#ef4444', darkTheme ? 0.16 : 0.12),
                                color: '#ef4444',
                                border: `1px solid ${withAlpha('#ef4444', 0.45)}`,
                            }}
                        >
                            {t('ログアウト')}
                        </Button>
                    </nav>
                </div>
            )}

            {/* Main Content */}
            <main className="container mx-auto p-4 md:p-8 relative z-0 flex-1 min-h-0 overflow-auto">
                <Outlet context={{ theme, user }} />
            </main>
        </div>
    );
}
